import { useEffect, useState } from 'react';

import { api, type Attekintes as AttekintesAdat } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Áttekintés — a program nyitóképernyője. (Megbízás 2.0 / 5.1)
 *
 * Nem új adatot mutat, hanem összeszedi, **mi vár tennivalóra**: melyik nap
 * maradt ellenőrizetlenül, melyik számla járt le, melyik szerződés fut ki.
 * Minden teendő végén gomb, ami a megfelelő szűrt nézetre visz — így a
 * reggeli végignézés egy képernyőről elintézhető.
 *
 * A színek jelentése ugyanaz, mint máshol: piros sürgős, sárga
 * figyelmeztetés, szürke adathiány.
 */

const SULY_CIMKE: Record<string, string> = {
  surgos: 'sürgős',
  figyelem: 'figyelem',
  adathiany: 'adathiány',
};

export function Attekintes({
  onUgras,
}: {
  onUgras: (kepernyo: string, opciok?: { datum?: string; szuro?: string }) => void;
}) {
  const [adat, setAdat] = useState<AttekintesAdat | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AttekintesAdat>('/api/attekintes')
      .then(setAdat)
      .catch((h: Error) => setHiba(h.message));
  }, []);

  if (hiba) {
    return <div className="uzenet bad">{hiba}</div>;
  }
  if (!adat) {
    return <div className="uzenet">Betöltés…</div>;
  }

  const napNeve = new Date(`${adat.datum}T00:00:00`).toLocaleDateString('hu-HU', {
    weekday: 'long',
  });

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Áttekintés</span>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
          {adat.datum} · {napNeve}
        </span>
      </div>

      <div className="cards">
        {adat.szamok.map((s) => (
          <div key={s.cimke} className={`card${s.kiemelt ? ' warn2' : ''}`}>
            <em>{s.cimke}</em>
            <b>{ft(s.ertek)}</b>
            <span>{s.megjegyzes}</span>
          </div>
        ))}
      </div>

      <h3 className="szakaszcim">
        Teendők
        {adat.teendok.length > 0 && <b>{ft(adat.teendok.length)}</b>}
      </h3>

      {adat.teendok.length === 0 ? (
        <div className="uzenet ok">
          Nincs teendő. Minden nap le van zárva, nincs lejárt számla és lejáró szerződés sem.
        </div>
      ) : (
        <div className="teendok">
          {adat.teendok.map((t, i) => (
            <div key={`${t.fajta}-${i}`} className={`teendo ${t.suly}`}>
              <span className="tsuly">{SULY_CIMKE[t.suly] ?? t.suly}</span>
              <span className="tszoveg">{t.szoveg}</span>
              {t.ugras && (
                <button
                  className="btn sm"
                  onClick={() =>
                    onUgras(t.ugras!.kepernyo, { datum: t.ugras!.datum, szuro: t.ugras!.szuro })
                  }
                >
                  {t.gomb}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {adat.honap && (
      <>
        <h3 className="szakaszcim">Ez a hónap — {adat.honap.cimke}</h3>
        <div className="cards">
          <div className="card">
            <em>Eladott mennyiség</em>
            <b>{ft(adat.honap.liter)}</b>
            <span>liter, a Napi bontással egyezően</span>
          </div>
          <div className="card">
            <em>Becsült érték</em>
            <b>{ft(adat.honap.ertek)}</b>
            <span>Ft, a lezárt napokból</span>
          </div>
          <div className="card">
            <em>Vásárolt partner</em>
            <b>{ft(adat.honap.vasarolt_partner)}</b>
            <span>ebben a hónapban</span>
          </div>
          <div className="card">
            <em>Napi átlag</em>
            <b>{ft(adat.honap.napi_atlag)}</b>
            <span>liter / forgalmas nap</span>
          </div>
        </div>
  
        <div className="uzenet flag" style={{ marginTop: 14 }}>
          Az <b>eladott mennyiség</b> ugyanaz, amit a Napi bontás és a havi zárás mutat: a lezárt
          napok és a régi Excelből behozott forgalmi előzmény együtt. A <b>becsült érték</b> csak a
          lezárt napokból jön, mert az előzményben nincs ár. Ami még ellenőrzés alatt van, az itt
          nem látszik — így a szám nem változik utólag magától.
        </div>
      </>
      )}
    </>
  );
}
